///
/// EarthBrowser
///
/// Created by Matt Giger on 1/24/2026
/// Copyright (c) 2026$ Geodata Labs
///

import { wgs } from "@/earthbrowser/utils/geographic"
import { type Geometry } from "@stovokor/shader/Geometry"
import { cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { sphere } from "@stovokor/math/sphere"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { cellToChildren, cellToLatLng, cellToVertexes, getRes0Cells, getResolution, type H3Index, vertexToLatLng } from "h3-js"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cPressureFieldAltitude = 5.0
const cAntimeridianThreshold = Math.PI * 0.5

export class PressureIsolineSphereGeometry implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public coordinate: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public available: boolean = true
	public bounds: cube
	public boundingSphere: sphere

	public readonly sphereRadius: number

	private vertexIndex = 0

	/**
	 * @param altitudeKm Shell altitude above the ellipsoid. Renderers may
	 * place vertices at a different radius through their sphereRadius
	 * uniform; this value sizes the culling bounds, so pass the maximum.
	 */
	constructor(context: StovokorContext, resolution: number = 1, altitudeKm: number = cPressureFieldAltitude) {
		this.coordinate = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.coordinate])

		const rad = trig.rad
		const res0 = getRes0Cells()
		const cells = res0.flatMap(index => this.getChildCells(index, resolution))

		for (const cell of cells) {
			this.buildCellTriangles(cell, rad)
		}

		this.sphereRadius = wgs.a + altitudeKm
		this.bounds = new cube(-this.sphereRadius, this.sphereRadius)
		this.boundingSphere = new sphere(this.sphereRadius)
	}

	private buildCellTriangles(cell: H3Index, rad: number): void {
		const cellLatLng = cellToLatLng(cell)
		const centerLon = cellLatLng[1] * rad
		const centerLat = cellLatLng[0] * rad

		const vertices = cellToVertexes(cell)
		const corners: Array<{ lon: number; lat: number }> = vertices.map(vertex => {
			const vertexLatLng = vertexToLatLng(vertex)
			return {
				lon: vertexLatLng[1] * rad,
				lat: vertexLatLng[0] * rad
			}
		})

		const allLons = [centerLon, ...corners.map(c => c.lon)]
		const crossesAntimeridian = this.crossesAntimeridian(allLons)

		let adjustedCenterLon = centerLon
		const adjustedCorners = corners.map(c => ({ ...c }))

		if (crossesAntimeridian) {
			if (centerLon < 0) {
				adjustedCenterLon = centerLon + 2 * Math.PI
			}
			for (const corner of adjustedCorners) {
				if (corner.lon < 0) {
					corner.lon = corner.lon + 2 * Math.PI
				}
			}
		}

		this.coordinate.add(adjustedCenterLon, centerLat)
		const centerIndex = this.vertexIndex++

		const cornerIndices: number[] = []
		for (const corner of adjustedCorners) {
			this.coordinate.add(corner.lon, corner.lat)
			cornerIndices.push(this.vertexIndex++)
		}

		for (let i = 0; i < cornerIndices.length - 1; i++) {
			this.indexBuffer.triangle(centerIndex, cornerIndices[i], cornerIndices[i + 1])
		}
		this.indexBuffer.triangle(centerIndex, cornerIndices[cornerIndices.length - 1], cornerIndices[0])
	}

	private crossesAntimeridian(lons: number[]): boolean {
		let hasPositive = false
		let hasNegative = false
		for (const lon of lons) {
			if (lon > cAntimeridianThreshold) hasPositive = true
			if (lon < -cAntimeridianThreshold) hasNegative = true
		}
		return hasPositive && hasNegative
	}

	private getChildCells(index: H3Index, targetRes: number): H3Index[] {
		const currentRes = getResolution(index)
		if (currentRes === targetRes) {
			return [index]
		}
		if (currentRes >= targetRes - 1) {
			return cellToChildren(index, targetRes)
		}
		return cellToChildren(index, currentRes + 1).flatMap(child =>
			this.getChildCells(child, targetRes)
		)
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

